<template>
  <div :class="outerContainerClasses">
    <div :class="containerClasses">
      <label :for="name" :class="labelClasses" :style="labelStyles">
        {{ label }}
        <span v-if="required && showRequiredIndicator" class="text-red-400 ml-1"
          >*</span
        >
      </label>

      <div :class="selectWrapperClasses">
        <multiselect
          v-if="useMultiselect"
          v-model="fieldValue"
          :options="options"
          :multiple="multiple"
          :track-by="optionValue"
          :label="optionLabel"
          :placeholder="placeholder"
          :disabled="disabled"
          :close-on-select="!multiple"
          :clear-on-select="false"
          :preserve-search="true"
          @close="handleBlur"
          :class="[
            'custom-multiselect',
            { 'has-error': hasError, 'has-warning': hasPrevalidationWarning, 'has-required': isRequiredEmpty },
          ]"
        >
          <template #selection="{ values, isOpen }">
            <span
              class="multiselect__single"
              v-if="values.length && !isOpen && !multiple"
            >
              {{ getOptionLabel(values[0]) }}
            </span>
          </template>
        </multiselect>

        <select
          v-else
          :id="name"
          :name="name"
          :class="[
            selectClasses,
            {
              'red-border': hasError,
              'warning-border': hasPrevalidationWarning,
              'input-required': isRequiredEmpty,
            },
          ]"
          :value="nativeSelectValue"
          @change="handleNativeChange"
          @blur="handleBlur"
          :style="computedSelectStyles"
          :disabled="disabled"
        >
          <option
            v-if="placeholder"
            :value="placeholderValue"
            disabled
            style="color: gray"
          >
            {{ placeholder }}
          </option>
          <option
            v-for="option in options"
            :key="getOptionKey(option)"
            :value="getOptionValue(option)"
          >
            {{ getOptionLabel(option) }}
          </option>
        </select>

        <div
          v-if="hasError && showErrors && errorPosition === 'absolute'"
          :class="errorMessageAbsoluteClasses"
          :style="errorMessageStyles"
        >
          {{ errorMessage }}
        </div>
        <div
          v-if="hasError && showErrors && errorPosition === 'below'"
          :class="errorMessageBelowClasses"
          :style="errorMessageStyles"
        >
          {{ errorMessage }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useField } from "vee-validate";
import { computed, toRef, watch } from "vue";
import { mergeStyles } from "@/validations/styles/themeConfig.js";
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.css";

export default {
  name: "ValidatedSelect",
  components: { Multiselect },
  props: {
    // ... all your existing props
    name: { type: String, required: true },
    label: { type: String, required: true },
    options: { type: Array, default: () => [] },
    placeholder: { type: String, default: "" },
    required: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    showErrors: { type: Boolean, default: false },
    showRequiredIndicator: { type: Boolean, default: false },
    errorPosition: { type: String, default: "below" },
    modelValue: { type: [String, Number, Object, Array], default: null },
    optionValue: { type: String, default: "id" },
    optionLabel: { type: String, default: "nome" },
    returnObject: { type: Boolean, default: true },
    rules: { type: [String, Object, Function], default: undefined },
    placeholderValue: { type: [String, Number], default: "" },
    customStyles: { type: Object, default: () => ({}) },
    useExistingStyle: { type: Boolean, default: true },
    showPrevalidationWarning: { type: Boolean, default: false },
    warningCondition: { type: Function, default: null },
    // NEW PROPS
    multiple: { type: Boolean, default: false },
    useMultiselect: { type: Boolean, default: true }, // Toggle for backward compatibility
  },
  emits: ["update:modelValue"],
  setup(props, { emit }) {
    const name = toRef(props, "name");

    const getInitialValue = () => {
      if (props.modelValue !== null && props.modelValue !== undefined)
        return props.modelValue;
      return props.multiple ? [] : props.placeholderValue;
    };

    const {
      value: fieldValue,
      errorMessage,
      handleBlur,
      handleChange: veeHandleChange,
      meta,
    } = useField(name, props.rules, {
      initialValue: getInitialValue(),
      validateOnValueUpdate: true,
      syncVModel: true,
    });

    // Native select needs a flat value (id or string), not an object
    const nativeSelectValue = computed(() => {
      if (
        props.returnObject &&
        fieldValue.value &&
        typeof fieldValue.value === "object"
      ) {
        return fieldValue.value[props.optionValue] || props.placeholderValue;
      }
      return fieldValue.value || props.placeholderValue;
    });

    const handleNativeChange = (event) => {
      const selectedValue = event.target.value;
      let finalValue = selectedValue;

      if (props.returnObject && selectedValue !== props.placeholderValue) {
        finalValue =
          props.options.find(
            (opt) => String(getOptionValue(opt)) === String(selectedValue),
          ) || selectedValue;
      }
      veeHandleChange(finalValue);
      emit("update:modelValue", finalValue);
    };

    // Resolve a primitive ID to the full option object (needed for vue-multiselect)
    const resolveToOption = (val) => {
      if (!props.returnObject || !props.useMultiselect) return val;
      if (val === null || val === undefined || typeof val === 'object') return val;
      return props.options.find(opt => String(opt[props.optionValue]) === String(val)) ?? val;
    };

    // Watch for external modelValue changes
    watch(
      () => props.modelValue,
      (newVal) => {
        const resolved = resolveToOption(newVal);
        if (JSON.stringify(resolved) !== JSON.stringify(fieldValue.value)) {
          fieldValue.value = resolved;
        }
      },
      { deep: true },
    );

    // When options load asynchronously, re-resolve if value is still a primitive
    watch(
      () => props.options,
      () => {
        if (!props.useMultiselect || !props.returnObject) return;
        const val = fieldValue.value;
        if (val !== null && val !== undefined && typeof val !== 'object') {
          const resolved = resolveToOption(val);
          if (resolved !== val) fieldValue.value = resolved;
        }
      },
    );

    const hasError = computed(
      () => !!(errorMessage.value && (meta.touched || meta.dirty)),
    );

    const isRequiredEmpty = computed(() => {
      if (!props.required || !props.showPrevalidationWarning || props.warningCondition) return false;
      const val = fieldValue.value;
      if (props.multiple) return !val || val.length === 0;
      return (
        val === props.placeholderValue ||
        val === null ||
        val === undefined ||
        (typeof val === "object" && (!val[props.optionValue] || val[props.optionValue] === 0))
      );
    });

    const hasPrevalidationWarning = computed(() => {
      if (!props.showPrevalidationWarning || !props.warningCondition) return false;
      return props.warningCondition(fieldValue.value);
    });

    const fieldState = computed(() => {
      if (props.disabled) return "disabled";
      if (hasError.value) return "error";
      if (hasPrevalidationWarning.value) return "warning";
      return "normal";
    });

    const styleConfig = computed(() => {
      const base = props.useExistingStyle
        ? {
            container: { classes: "flex justify-between items-center" },
            label: {
              classes: "text-blue-100 text-right w-1/6",
              styles: "color: var(--label);",
            },
            select: {
              classes:
                "rounded p-2 placeholder-gray-500 w-full text-xs",
              styles: {
                normal: {},
                warning: {
                  backgroundColor: "var(--aviso, #FEF3C7)",
                  color: "var(--text)",
                  border: "1px solid var(--aviso-border, #F59E0B)",
                },
                error: {
                  backgroundColor: "var(--obrigatorio)",
                  color: "var(--text)",
                },
              },
            },
            errorMessageAbsolute: {
              classes:
                "absolute top-full left-0 text-xs mt-1 z-10 bg-white px-2 py-1 rounded shadow-md border border-red-300",
              styles: "color: var(--vermelho-2);",
            },
            errorMessageBelow: {
              classes: "text-xs mt-1 w-full",
              styles: "color: var(--vermelho-2);",
            },
            errorMessage: { styles: "color: var(--vermelho-2);" },
          }
        : mergeStyles(props.customStyles);
      return base;
    });

    const getOptionKey = (opt) =>
      typeof opt === "object" ? opt[props.optionValue] : opt;
    const getOptionValue = (opt) =>
      typeof opt === "object" ? opt[props.optionValue] : opt;
    const getOptionLabel = (opt) =>
      typeof opt === "object"
        ? opt[props.optionLabel] || opt.nome || opt.name
        : opt;

    return {
      fieldValue,
      nativeSelectValue,
      errorMessage,
      handleBlur,
      handleNativeChange,
      meta,
      hasError,
      isRequiredEmpty,
      hasPrevalidationWarning,
      fieldState,
      styleConfig,
      getOptionLabel,
      getOptionKey,
      getOptionValue,
      outerContainerClasses: "relative",
      containerClasses: computed(() => styleConfig.value.container.classes),
      labelClasses: computed(() => styleConfig.value.label.classes),
      labelStyles: computed(() => styleConfig.value.label.styles),
      selectWrapperClasses: computed(() => {
        const customSelectWrapperClasses = styleConfig.value.selectWrapper?.classes || 'w-3/4';
        return props.errorPosition === 'absolute' ? `relative ${customSelectWrapperClasses}` : customSelectWrapperClasses;
      }),
      selectClasses: computed(() => styleConfig.value.select.classes),
      computedSelectStyles: computed(
        () => styleConfig.value.select.styles[fieldState.value],
      ),
      errorMessageAbsoluteClasses: computed(
        () => styleConfig.value.errorMessageAbsolute.classes,
      ),
      errorMessageBelowClasses: computed(
        () => styleConfig.value.errorMessageBelow.classes,
      ),
      errorMessageStyles: computed(() => styleConfig.value.errorMessage.styles),
    };
  },
};
</script>

<style scoped>
/* NATIVE SELECT CLASSES */
.red-border {
  background-color: var(--obrigatorio);
}
.warning-border {
  background-color: var(--aviso, #fef3c7);
  border: 1px solid var(--aviso-border, #f59e0b);
}
select:focus {
  outline: none;
}

/* MULTISELECT OVERRIDES */
:deep(.custom-multiselect) {
  width: 100%;
}
:deep(.custom-multiselect .multiselect__tags) {
  border: 1px solid var(--glass-border);
  border-radius: 4px;
  background-color: var(--fundoForm);
  min-height: 32px;
  padding-top: 4px;
}
:deep(.custom-multiselect .multiselect__input) {
  color: var(--text) !important;
  background: transparent !important;
}
:deep(.custom-multiselect .multiselect__single) {
  color: var(--text) !important;
  font-size: 1rem;
  background: transparent !important;
}
:deep(.custom-multiselect .multiselect__tag) {
  background: #e5e7eb;
  color: #000000;
  border: 1px solid #d1d5db;
  /* font-size: 0.75rem; */
  margin-bottom: 2px;
}
:deep(.has-error .multiselect__tags) {
  background-color: var(--obrigatorio) !important;
  border: 1px solid #fca5a5 !important;
}
:deep(.has-required .multiselect__tags) {
  background-color: var(--obrigatorio) !important;
  border-color: rgba(255, 100, 80, 0.55) !important;
}
:deep(.has-warning .multiselect__tags) {
  background-color: var(--aviso, #fef3c7) !important;
  border: 1px solid var(--aviso-border, #f59e0b) !important;
}
:deep(.multiselect__placeholder) {
  /* font-size: 1rem; */
  color: #6b7280 !important;
}
</style>
